/**
 * SIGN IN.
 *
 * The UI implements NO authentication logic. It collects what the user types, posts it, and renders
 * what came back. It does not decide whether the credentials are good, it does not decide what the
 * user may then see, and it stores no token — the session is a first-party cookie the server sets.
 *
 * 09 §5.8: a failure here is a written Hebrew sentence, never a status code and never an exception.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { DeniedError } from '../api/client';
import { callOperation } from '../api/operations';
import { DenialNotice } from '../components/DenialNotice';
import type { DenyBody } from '../api/contract';

/**
 * The server's four-arm answer to a sign-in. It now lives beside the operation that produces it, in
 * `api/operations.ts`, so the request shape and the response shape are declared together and the
 * mirror test can reconcile both against `apps/api/src/routes/public.ts`.
 */
export type { SignInResponse } from '../api/operations';

export interface SignInScreenProps {
  readonly onSignedIn: () => void;
  /** The principal holds a second factor and must present it. No session exists yet. */
  readonly onMfaRequired: (challengeRef: string) => void;
  /** The principal is REQUIRED to hold one and does not. Enrolment is unavoidable, not offered. */
  readonly onMfaEnrolmentRequired: (challengeRef: string) => void;
}

export function SignInScreen({
  onSignedIn,
  onMfaRequired,
  onMfaEnrolmentRequired,
}: SignInScreenProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [deny, setDeny] = useState<DenyBody | null>(null);
  const [failedHe, setFailedHe] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  /**
   * Ask for a reset mail, and say the same thing however it goes.
   *
   * `resetSent` is set on BOTH arms on purpose. A screen that reported a failure here would be
   * distinguishing outcomes the endpoint spent its design refusing to distinguish — and the one
   * thing a person can act on, "check your inbox", is true in every case worth telling them about.
   */
  async function requestReset(): Promise<void> {
    setDeny(null);
    setFailedHe(null);
    try {
      await callOperation('auth.password.reset_request', { body: { login: email } });
    } catch {
      // Swallowed, and this is the rare place that is correct. See above.
    } finally {
      setResetSent(true);
    }
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setDeny(null);
    setFailedHe(null);
    try {
      /*
       * THREE DEFECTS ON THE PRODUCT'S FRONT DOOR, all found by following the wire rather than by
       * reading either side alone. Each was invisible to the other end.
       *
       *   1. The path was `/session`. The declared route is `/api/auth/session`. There is no
       *      `/api/session`, so every sign-in 404'd. **Neither defect is writable here any more:**
       *      the address is built by `callOperation` from the approved baseline, and the body shape
       *      is `OperationBodies['auth.session.create']`, reconciled against the handler's own
       *      field names by `reachability.test.ts`.
       *   2. The body was `{ email, password }`. The route reads `{ login, password }`, so even at
       *      the right URL the credential arrived as `undefined` and the answer would have been the
       *      generic 401 the endpoint returns for a wrong password — indistinguishable from a real
       *      failure, which is exactly what makes it survivable for months.
       *   3. `onSignedIn()` was called UNCONDITIONALLY. `signIn` returns a four-arm union, and two
       *      of those arms are MFA challenges that deliberately set NO COOKIE. The screen would have
       *      announced a successful sign-in to a principal holding no session.
       *
       * A write. It is never retried — see the retry policy in api/client.ts.
       */
      const result = await callOperation('auth.session.create', {
        body: { login: email, password },
      });

      // The union is exhaustive on purpose (see routes/public.ts): "I forgot the MFA branch" is what
      // defect 3 was, and it does not compile here either.
      switch (result.outcome) {
        case 'SESSION':
          onSignedIn();
          return;
        case 'MFA_REQUIRED':
          onMfaRequired(result.challengeRef);
          return;
        case 'MFA_ENROLMENT_REQUIRED':
          onMfaEnrolmentRequired(result.challengeRef);
          return;
      }
    } catch (err: unknown) {
      if (err instanceof DeniedError) {
        setDeny(err.body);
      } else {
        setFailedHe('לא הצלחנו להתחבר. נסה שוב בעוד רגע.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="signin" dir="rtl">
      <h1 className="signin__title">GL-POINT</h1>
      <p className="signin__lead">התחברות לסביבת המשרד</p>

      <form className="signin__form" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">דואר אלקטרוני</span>
          <input
            className="field__input"
            type="email"
            dir="ltr"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            required
          />
        </label>

        <label className="field">
          <span className="field__label">סיסמה</span>
          <input
            className="field__input"
            type="password"
            dir="ltr"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            required
          />
        </label>

        <button type="submit" className="btn btn--primary" disabled={pending}>
          {pending ? 'מתחבר…' : 'התחברות'}
        </button>
      </form>

      {/*
        ── FORGOT MY PASSWORD. **G-10 in the ledger, closed at the wire.** ────────────────────────

        The ledger's words were: *"a lost authenticator with spent recovery codes is a permanent
        lockout"*. The route was declared and public, and nothing in this app addressed it — so an
        office that lost a password had exactly one route back into their own accounting system,
        which was to telephone somebody.

        ── THE ANSWER IS THE SAME WHETHER OR NOT THE ADDRESS EXISTS ───────────────────────────────

        Deliberately, and the handler enforces it: even a malformed body gets the uniform 202.
        Anything else is an account-enumeration oracle — a stranger typing addresses into this form
        would learn which of them belong to a client of this firm, which is a client list.

        So the screen says "if that address belongs to an account, a message has been sent". It is
        careful phrasing rather than vagueness for its own sake, and the sentence is the same one
        whichever answer came back, because there is only one answer.
      */}
      {resetSent ? (
        <p className="signin__failed" role="status">
          אם הכתובת הזו שייכת לחשבון — נשלחה אליה הודעה עם קישור לקביעת סיסמה חדשה. הקישור מגיע לתיבת
          הדואר בלבד ואינו מוצג כאן. אם לא הגיעה הודעה, ייתכן שהכתובת אינה מוכרת לנו.
        </p>
      ) : (
        <button
          type="button"
          className="btn btn--quiet"
          disabled={pending || email.trim() === ''}
          onClick={() => void requestReset()}
        >
          שכחתי את הסיסמה
        </button>
      )}

      {deny === null ? null : <DenialNotice deny={deny} />}
      {failedHe === null ? null : <p className="signin__failed">{failedHe}</p>}
    </main>
  );
}
